/**
 * Field and page visibility helpers for ChoiceFlow
 */

/**
 * Check if visibility condition matches current answers
 * @param {object} condition - Visibility condition object
 * @param {object} answers - Current form answers
 * @returns {boolean} Whether condition is satisfied
 */
export const matchesVisibilityCondition = (condition, answers) => {
  if (!condition || typeof condition !== 'object') {
    return true; // No condition means always visible
  }

  // All key-value pairs in condition must match answers
  return Object.entries(condition).every(([fieldId, expectedValue]) => {
    const actualValue = answers[fieldId];

    // Handle different types of matching
    if (Array.isArray(expectedValue)) {
      // Expected value is array - actual value must be in the array
      return expectedValue.includes(actualValue);
    }

    if (Array.isArray(actualValue)) {
      // Actual value is array - expected value must be in the array
      return actualValue.includes(expectedValue);
    }

    // Simple equality check
    return actualValue === expectedValue;
  });
};

/**
 * Check if a field should be visible based on its visibleWhen condition
 * @param {object} field - Field definition
 * @param {object} answers - Current form answers
 * @returns {boolean} Whether the field should be visible
 */
export const isFieldVisible = (field, answers) => {
  if (!field) {
    return false;
  }

  // If no visibility condition, field is always visible
  if (!field.visibleWhen) {
    return true;
  }

  // Check if all conditions in visibleWhen are met
  return matchesVisibilityCondition(field.visibleWhen, answers);
};

/**
 * Get all visible fields from a list based on current answers
 * @param {Array} fields - Array of field definitions
 * @param {object} answers - Current form answers
 * @returns {Array} Array of visible field definitions
 */
export const getVisibleFields = (fields, answers) => {
  if (!Array.isArray(fields)) {
    return [];
  }

  return fields.filter((field) => isFieldVisible(field, answers));
};

/**
 * Check if a page should be visible based on its conditions
 * (This is for future enhancement - currently all pages are visible)
 * @param {object} page - Page definition
 * @returns {boolean} Whether the page should be visible
 */
export const isPageVisible = (page) => {
  if (!page) {
    return false;
  }

  // For now, all pages are visible
  // This can be enhanced later to support conditional page visibility
  // based on page.visibleWhen or similar conditions
  return true;
};

/**
 * Get count of visible required fields on a page
 * @param {Array} fields - Array of field definitions
 * @param {object} answers - Current form answers
 * @returns {number} Count of visible required fields
 */
export const getVisibleRequiredFieldCount = (fields, answers) => {
  const visibleFields = getVisibleFields(fields, answers);
  return visibleFields.filter((field) => field.required).length;
};

/**
 * Get count of visible required fields that have been answered
 * @param {Array} fields - Array of field definitions
 * @param {object} answers - Current form answers
 * @returns {number} Count of answered visible required fields
 */
export const getAnsweredRequiredFieldCount = (fields, answers) => {
  const visibleFields = getVisibleFields(fields, answers);
  return visibleFields.filter((field) => {
    if (!field.required) return false;

    const value = answers[field.id];

    // Check if field has a meaningful value
    if (value === null || value === undefined) return false;
    if (value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;

    return true;
  }).length;
};

/**
 * Check if all visible required fields on a page are answered
 * @param {Array} fields - Array of field definitions
 * @param {object} answers - Current form answers
 * @returns {boolean} Whether all visible required fields are answered
 */
export const areAllRequiredFieldsAnswered = (fields, answers) => {
  const requiredCount = getVisibleRequiredFieldCount(fields, answers);
  const answeredCount = getAnsweredRequiredFieldCount(fields, answers);
  return requiredCount === answeredCount;
};

/**
 * Get field IDs that should trigger visibility recalculation for other fields
 * This helps optimize when to recalculate field visibility
 * @param {Array} fields - Array of field definitions
 * @returns {Set} Set of field IDs that affect visibility of other fields
 */
export const getVisibilityTriggerFields = (fields) => {
  const triggerFields = new Set();

  if (!Array.isArray(fields)) {
    return triggerFields;
  }

  fields.forEach((field) => {
    if (field.visibleWhen && typeof field.visibleWhen === 'object') {
      Object.keys(field.visibleWhen).forEach((fieldId) => {
        triggerFields.add(fieldId);
      });
    }
  });

  return triggerFields;
};

/**
 * Check if a field change should trigger visibility recalculation
 * @param {string} changedFieldId - ID of the field that changed
 * @param {Array} fields - Array of field definitions
 * @returns {boolean} Whether visibility should be recalculated
 */
export const shouldRecalculateVisibility = (changedFieldId, fields) => {
  const triggerFields = getVisibilityTriggerFields(fields);
  return triggerFields.has(changedFieldId);
};
